/**
 * The three sounds the front desk hears.
 *
 * "The chat bell is not working 🤔 I checked the speakers … Ok it's working
 * but faint cal we get a stronger tone and a door bell for any txt or email
 * from a client and a different sound for new client form submissions."
 *
 * So: louder than before, and three sounds you can tell apart from across the
 * room without looking. They are generated rather than shipped as files, which
 * keeps them out of the bundle and lets the shape be tuned in one place.
 *
 * Why one shared AudioContext: Chrome caps how many a page may create (around
 * six) and a busy morning would otherwise exhaust them and go silent. It is
 * created on first use and resumed on demand, because browsers keep it
 * suspended until the page has been interacted with.
 */

let ctx: AudioContext | null = null;

function audio(): AudioContext | null {
  try {
    if (!ctx) {
      const Ctx = window.AudioContext
        ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctx) return null;
      ctx = new Ctx();
    }
    // Autoplay policy parks it until a click happens somewhere on the page.
    if (ctx.state === "suspended") void ctx.resume().catch(() => {});
    return ctx;
  } catch {
    return null;
  }
}

/**
 * One note. `type` shapes the timbre, `peak` the loudness, and the envelope
 * keeps it from clicking — a square-edged gain change is audible as a pop.
 */
function note(opts: {
  hz: number; at: number; dur: number; peak: number;
  type?: OscillatorType; detuneHz?: number;
}): void {
  const c = audio();
  if (!c) return;
  const t0 = c.currentTime + opts.at;
  const osc = c.createOscillator();
  const gain = c.createGain();
  osc.type = opts.type ?? "sine";
  osc.frequency.value = opts.hz;

  // A second, slightly detuned voice gives the tone some body so it carries
  // in a room, without having to simply crank the volume.
  const osc2 = opts.detuneHz ? c.createOscillator() : null;
  if (osc2) {
    osc2.type = opts.type ?? "sine";
    osc2.frequency.value = opts.hz + opts.detuneHz!;
  }

  gain.gain.setValueAtTime(0.0001, t0);
  gain.gain.exponentialRampToValueAtTime(opts.peak, t0 + 0.012);
  gain.gain.exponentialRampToValueAtTime(0.0001, t0 + opts.dur);

  osc.connect(gain);
  osc2?.connect(gain);
  gain.connect(c.destination);
  osc.start(t0); osc.stop(t0 + opts.dur + 0.02);
  osc2?.start(t0); osc2?.stop(t0 + opts.dur + 0.02);
}

/**
 * Team chat. The two notes she already knows, but with some weight behind
 * them — the old one peaked at 0.16, which she could not hear over a room.
 */
export function playChatChime(): void {
  note({ hz: 880,    at: 0,    dur: 0.26, peak: 0.42, detuneHz: 1.5 });
  note({ hz: 1174.7, at: 0.13, dur: 0.30, peak: 0.42, detuneHz: 1.5 });
}

/**
 * A client texted or emailed. An actual doorbell: the falling two-tone
 * ding-dong everybody already reads as "somebody is at the door", with a long
 * decay so it rings rather than beeps.
 */
export function playDoorbell(): void {
  note({ hz: 659.25, at: 0,    dur: 0.75, peak: 0.52, type: "triangle", detuneHz: 2 });
  note({ hz: 523.25, at: 0.34, dur: 1.05, peak: 0.52, type: "triangle", detuneHz: 2 });
}

/**
 * A client finished a form. Rising rather than falling, so it cannot be
 * mistaken for the doorbell even when both land in the same minute.
 */
export function playFormChime(): void {
  note({ hz: 587.33, at: 0,    dur: 0.22, peak: 0.34, detuneHz: 1 });
  note({ hz: 783.99, at: 0.11, dur: 0.22, peak: 0.36, detuneHz: 1 });
  note({ hz: 1046.5, at: 0.22, dur: 0.46, peak: 0.40, detuneHz: 1 });
}

/** Her per-browser mute, shared by everything above. */
const SOUND_KEY = "hh-chat-sound";

export function soundsOn(): boolean {
  try { return localStorage.getItem(SOUND_KEY) !== "off"; } catch { return true; }
}

export function setSoundsOn(on: boolean): void {
  try { localStorage.setItem(SOUND_KEY, on ? "on" : "off"); } catch { /* private window */ }
}

/** Play one only if sound is on — what every caller actually wants. */
export function ping(which: "chat" | "doorbell" | "form"): void {
  if (!soundsOn()) return;
  if (which === "chat") playChatChime();
  else if (which === "doorbell") playDoorbell();
  else playFormChime();
}
